import { useActiveAccount } from "applesauce-react/hooks";
import { USER_BLOSSOM_SERVER_LIST_KIND } from "blossom-client-sdk";
import { kinds } from "nostr-tools";
import { PropsWithChildren, useEffect, useRef } from "react";

import useReplaceableEvent from "../../hooks/use-replaceable-event";
import { replaceableLoader } from "../../services/loaders";
import { usePublishEvent } from "./publish-provider";

const DEFAULT_RELAY = "wss://nostr.litstrudel.xyz";
const DEFAULT_BLOSSOM = "https://blossom.litstrudel.xyz/";

/** D-tags for synced settings (kind 30078, NIP-44 self-encrypted) */
const SYNCED_SETTING_DTAGS = [
  "litstrudel/nwc-wallet",
  "litstrudel/content-policy",
  "litstrudel/lookup-config",
  "litstrudel/dm-prefs",
  "litstrudel/relay-auth",
  "litstrudel/relay-config",
  "nostrudel-settings",
];

/**
 * Pre-fetches all synced settings from relays on login so they're warm
 * by the time components mount.
 */
function SyncedStateHydrator() {
  const account = useActiveAccount();
  const hydrated = useRef(new Set<string>());

  useEffect(() => {
    if (!account) return;
    const pubkey = account.pubkey;
    if (hydrated.current.has(pubkey)) return;
    hydrated.current.add(pubkey);

    // Fire all replaceable loader calls in parallel with 10s timeout
    const subs = SYNCED_SETTING_DTAGS.map((dtag) =>
      replaceableLoader({ kind: 30078, pubkey, identifier: dtag }).subscribe(),
    );

    const timer = setTimeout(() => {
      subs.forEach((s) => s.unsubscribe());
    }, 10_000);

    return () => {
      clearTimeout(timer);
      subs.forEach((s) => s.unsubscribe());
    };
  }, [account]);

  return null;
}

/**
 * Auto-publishes default relay list (kind 10002) and blossom server list (kind 10063)
 * for accounts that don't have them yet. Runs after sign-in.
 */
function BootstrapWatcher() {
  const account = useActiveAccount();
  const publish = usePublishEvent();
  const bootstrapped = useRef(new Set<string>());

  const relayList = useReplaceableEvent(
    account ? { kind: kinds.RelayList, pubkey: account.pubkey } : undefined,
  );
  const blossomList = useReplaceableEvent(
    account ? { kind: USER_BLOSSOM_SERVER_LIST_KIND, pubkey: account.pubkey } : undefined,
  );

  useEffect(() => {
    if (!account) return;
    const key = account.pubkey;
    if (bootstrapped.current.has(key)) return;

    // Wait for events to load from relays before deciding they're missing
    const timeout = setTimeout(async () => {
      if (bootstrapped.current.has(key)) return;
      bootstrapped.current.add(key);

      try {
        if (!relayList) {
          await publish(
            "Set Mailbox Relays",
            { kind: kinds.RelayList, created_at: Math.floor(Date.now() / 1000), content: "", tags: [["r", DEFAULT_RELAY]] },
            [DEFAULT_RELAY],
          );
        }

        if (!blossomList) {
          await publish(
            "Set Media Servers",
            { kind: USER_BLOSSOM_SERVER_LIST_KIND, created_at: Math.floor(Date.now() / 1000), content: "", tags: [["server", DEFAULT_BLOSSOM]] },
            [DEFAULT_RELAY],
          );
        }
      } catch (e) {
        // Remove from bootstrapped so it can retry
        bootstrapped.current.delete(key);
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [account, relayList, blossomList, publish]);

  return null;
}

export default function AccountBootstrapProvider({ children }: PropsWithChildren) {
  return (
    <>
      <SyncedStateHydrator />
      <BootstrapWatcher />
      {children}
    </>
  );
}
